import * as React from "react";
import { ApolloClient } from "apollo-client";
import { withApollo } from "react-apollo";
import { History } from "history";
import * as yup from "yup";
import { Helmet } from "react-helmet";

import { setAuthResponse } from "../session";
import { dashboardUrl } from "../urls";
import {
  userSignInWithPassword,
  UserCreate,
  userCreate,
} from "../graphql/user";
import {
  Button,
  Container,
  Divider,
  Grid,
  Message,
  Responsive,
  Segment,
} from "../semantic-ui";
import { Formik, Form } from "../formik";

type Props = {
  client: ApolloClient<any>;
  history: History;
};

type State = {
  error: string | null;
};

type Values = {
  email: string;
  password: string;
  name: string;
};

const loginSchema = yup.object().shape({
  email: yup.string().required().email(),
  password: yup.string().required(),
});

const createSchema = loginSchema.shape({
  password: yup.string().required().min(8),
  name: yup.string().required().min(5),
});

const initialValues = { email: "", password: "", name: "" };

class LoginPage extends React.Component<Props, State> {
  state = { error: null };

  handleLogin = (values: Values, { setSubmitting }: any) => {
    this.props.client
      .mutate({
        mutation: userSignInWithPassword,
        variables: values,
      })
      .then(this.handleAuthResponse)
      .catch((err: any) => {
        if (
          err.graphQLErrors &&
          err.graphQLErrors[0].message === "NotFoundError" &&
          err.graphQLErrors[0].path.length === 1
        ) {
          this.setState({ error: "Invalid email / password" });
        } else {
          this.setState({ error: err.message });
        }
        setSubmitting(false);
      });
  };

  handleCreate = (values: Values) => {
    this.props.client
      .mutate({
        mutation: userCreate,
        variables: values,
      })
      .then(this.handleAuthResponse)
      .catch((err: any) => {
        if (
          err.graphQLErrors &&
          err.graphQLErrors[0].message === "NotFoundError" &&
          err.graphQLErrors[0].path.length === 1
        ) {
          this.setState({ error: "Invalid email / password" });
        } else {
          this.setState({ error: err.message });
        }
      });
  };

  handleAuthResponse = (response: { data?: UserCreate }) => {
    const { token, expiresAt } = response.data!.authResponse;
    setAuthResponse({ token, expiresAt });
    // Delay a bit to allow Apollo or else the dashboard query will not pick up
    // the new authResponse.
    setTimeout(() => this.props.history.push(dashboardUrl), 10);
  };

  render() {
    return (
      <Container text>
        <Helmet title="Login" />
        {this.state.error && <Message error>{this.state.error}</Message>}
        <Segment placeholder style={{ margin: "20px 0" }}>
          <Grid columns={2} relaxed="very" stackable>
            <Grid.Column>
              <Formik
                onSubmit={this.handleLogin}
                initialValues={initialValues}
                validationSchema={loginSchema}
              >
                <Form>
                  <h1>Sign In</h1>
                  <Form.Input
                    id="login-email"
                    icon="user"
                    iconPosition="left"
                    label="Email"
                    name="email"
                    type="email"
                  />
                  <Form.Input
                    id="login-password"
                    icon="lock"
                    iconPosition="left"
                    label="Password"
                    name="password"
                    type="password"
                  />
                  <Form.Field control={Button} type="submit" primary>
                    Sign In
                  </Form.Field>
                </Form>
              </Formik>
            </Grid.Column>
            <Grid.Column>
              <Formik
                onSubmit={this.handleCreate}
                initialValues={initialValues}
                validationSchema={createSchema}
              >
                <Form>
                  <h1>Create Account</h1>
                  <Form.Input
                    id="create-email"
                    label="Email"
                    name="email"
                    type="email"
                  />
                  <Form.Input
                    id="create-password"
                    label="Password"
                    name="password"
                    type="password"
                  />
                  <Form.Input
                    id="create-name"
                    label="Display Name"
                    name="name"
                  />
                  <Form.Field control={Button} type="submit">
                    Create New Account
                  </Form.Field>
                </Form>
              </Formik>
            </Grid.Column>
          </Grid>
          <Responsive as={Divider} minWidth={768} vertical>
            Or
          </Responsive>
        </Segment>
      </Container>
    );
  }
}

export default withApollo(LoginPage);
